import {
  Children,
  ReactNode,
  createElement,
  useState,
  useEffect,
  useRef,
  DOMAttributes,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { TileCarouselRef, TileCarouselProps } from './';
import { PageIndicatorProps } from './__internal__/types';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { useIsRTL } from '../../locale';
import { useRefArray } from '../../hooks/useRefArray';
import { postfixTestId } from '../../util/testId';
import bundle from './NovaComponents-TileCarousel-gui-icu.properties';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { Localizer } from '../../locale/useTextLocalizer';
import { ZeroState } from '../ZeroState';
import { Scrollable } from '../Scrollable';
import { getLogger } from '@sas/nova-core/logging';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { Flow } from '../Layouts/Flow';
import { Icon } from '../Icon';
import { Add, AddToInsights, PageControlDot, PageControlDotSelected } from '@sas/react-svg';
import { SelectInfo, Tile } from '../Tile';
import { Button, ButtonVariant } from '../Button';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { ReactKeyboardEvent } from '../../util/events';
import { getOSName, OSName } from '../../util';
import { useEarcon, EarconName } from '../../hooks/useEarcon';
import cx from 'classnames';
import './TileCarousel.css';
import cn from './TileCarousel.css.json';

const useTextLocalizer = createUseTextLocalizer(bundle);
const logger = getLogger('TileCarousel');

export const TileCarousel = forwardRef<TileCarouselRef, TileCarouselProps>(function TileCarousel(
  { pageIndicatorLocation = 'below', ...props },
  ref
) {
  const isRTL = useIsRTL();
  const localize = useTextLocalizer();
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(
    {
      'spacing-s': dt.spacing.core.s,
      'spacing-m': dt.spacing.core.m,
      'add-button-bd': dt.c.colorUiBorder,
    },
    'tile-carousel'
  );
  const { ariaLabel } = getCarouselA11yInfo(localize, props);
  const directSpeech = useDirectSpeech();
  const [scrollIndex, setScrollIndex] = useState(props.initialFocusableIndex ?? 0);
  const [focusableIndex, setFocusableIndex] = useState(props.initialFocusableIndex ?? 0);
  const scrollableContentRef = useRef<HTMLElement>(null);
  const earcon = useEarcon({ source: EarconName.InputFull });

  const allowDeselection = props.selectionMode === 'single' ? props.allowDeselection ?? true : true;

  useImperativeHandle(ref, () => ({
    scrollTo: ({ tileIndex, pageIndex }) => {
      if (tileIndex !== undefined) {
        setScrollIndex(tileIndex);
      } else if (pageIndex !== undefined) {
        setScrollIndex(pageIndex * tilesPerPage);
      }
    },
    setFocusableTile: setFocusableIndex,
  }));
  // This is updated to the height of an actual tile.  We can then use this to size
  // the ZeroStateMessage so the whole carousel keeps the same height if all tiles are
  // removed.
  const [zeroStateHeight, setZeroStateHeight] = useState(0);
  const [scrollWindowWidth, setScrollWindowWidth] = useState(0);
  const [tileWidth, setTileWidth] = useState(0);
  const tilesPerPage = tileWidth ? Math.floor(scrollWindowWidth / tileWidth) : 0;

  const selectionAnchorIndexRef = useRef(0);
  const selectionMode = props.selectionMode ?? 'multi';
  const hideCheckbox =
    selectionMode === 'none' || selectionMode === 'single' || selectionMode === 'desktop';
  const getContinuousSelectionIndices = (index: number) => {
    const startIndex = Math.min(selectionAnchorIndexRef.current, index);
    const endIndex = Math.max(selectionAnchorIndexRef.current, index);
    return [...Array(endIndex - startIndex + 1)].map((_, i) => startIndex + i);
  };
  const isMacOS = getOSName() === OSName.Mac;
  const onSelect = (index: number, e: SelectInfo) => {
    if (selectionMode === 'none' || !props.tiles) return;

    const continuousSelectionIndices = e.shiftKey
      ? getContinuousSelectionIndices(index)
      : undefined;
    const isCtrlOrCmdPressed = isMacOS ? e.metaKey : e.ctlKey;
    const shouldDeselectOtherTiles =
      selectionMode === 'single' || (e.selectionArea === 'tile' && !isCtrlOrCmdPressed);
    const toDeselectIndices: number[] = shouldDeselectOtherTiles
      ? props.tiles.reduce<number[]>(
          (prev, tile, i) =>
            tile.selected &&
            // Do not deselect the tiles in the continuous selection range
            !continuousSelectionIndices?.includes(i) &&
            // Do not deselect the current tile
            i !== index
              ? [...prev, i]
              : prev,
          []
        )
      : [];

    if (continuousSelectionIndices) {
      //  For continuous tile selection select all the tile in the current range if it is not selected
      for (const tileIndex of continuousSelectionIndices) {
        const tile = props.tiles[tileIndex];
        if (!tile.selected) {
          tile.onSelect?.({ index: tileIndex, selectionArea: 'checkbox' });
        }
      }
    } else {
      // For single tile selection, keep the current tile selected if deselection is not allowed
      // or other tiles are deselected by this selection action
      const shouldKeepSelected =
        props.tiles[index].selected && (!allowDeselection || toDeselectIndices.length > 0);
      if (!shouldKeepSelected) {
        props.tiles[index].onSelect?.(e);
      }
      // Update the selection anchor to be the current tile
      selectionAnchorIndexRef.current = index;
    }

    for (const tileIndex of toDeselectIndices) {
      props.tiles[tileIndex].onSelect?.({ index: tileIndex, selectionArea: 'checkbox' });
    }
  };

  // There's a problem with this ref array.  If the container removes a tile, the tileContainerRefs length
  // doesn't change but the entry becomes undefined.  So, referencing the tileContainerRefs.length will
  // result in an invalid number.
  const {
    refs: tileContainerRefs,
    createSetRef: createTileContainerRef,
  } = useRefArray<HTMLDivElement>();

  const tileNodes: ReactNode[] =
    props.tiles
      ?.slice(0, 100) // TileCarousel only shows the first 100 tiles
      .map((tileProps, index) => (
        <Tile
          {...tileProps}
          disableInteractiveMode={props.disableInteractiveMode}
          hideCheckbox={hideCheckbox}
          onSelect={(e) => onSelect(index, e)}
          tabIndex={index === focusableIndex ? 0 : -1}
        />
      )) ?? Children.toArray(props.children).slice(0, 100);
  const numberOfTiles = tileNodes.length;
  const numberOfPages = tilesPerPage ? Math.ceil(numberOfTiles / tilesPerPage) : 0;
  // Assume each page indicator dot takes 18px
  const hasSpaceForPageIndicator = scrollWindowWidth > numberOfPages * 18;
  const showPageIndicator =
    props.showPageIndicator && hasSpaceForPageIndicator && numberOfPages > 1;
  const showAddButton = !!props.onAdd;
  const addButtonTooltip =
    props.addButtonProps?.tooltip ?? localize('addTile.button.tooltip.label');
  const onAddTile = async () => {
    const { status, message } = (await props.onAdd?.()) ?? {};
    if (message) {
      directSpeech?.notify(message);
    } else if (status === 'success') {
      directSpeech?.notify(localize('tileAdded.aria'));
    }
  };
  const [setScrollWindowRef, getScrollWindow] = useResizeObserverRef<HTMLDivElement>(() => {
    const scrollWindow = getScrollWindow();
    setZeroStateHeight(scrollWindow?.offsetHeight ?? 0);
    const scrollWindowWidth = scrollWindow?.clientWidth ?? 0;
    if (scrollWindowWidth <= 0) return;

    // Assume all the tiles have the same width, only measure the width of the first tile
    setTileWidth(getTileWidth(tileContainerRefs?.[0]));
    setScrollWindowWidth(scrollWindowWidth);
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown': /* falls-through */
      case 'ArrowUp': /* falls-through */
      case 'ArrowLeft': /* falls-through */
      case 'ArrowRight':
        const offset =
          e.key === 'ArrowDown' ||
          (isRTL && e.key === 'ArrowLeft') ||
          (!isRTL && e.key === 'ArrowRight')
            ? 1
            : -1;

        e.persist();
        setFocusableIndex((prevIndex) => {
          const nextIndex = Math.max(0, Math.min(numberOfTiles - 1, prevIndex + offset));
          if (e.shiftKey) {
            onSelect(nextIndex, {
              selectionArea: 'tile',
              ctlKey: e.ctrlKey,
              metaKey: e.metaKey,
              shiftKey: e.shiftKey,
              altKey: e.altKey,
            });
          }
          // Sounds earcon when going beyond the beginning or end of tile carousel
          if (prevIndex === nextIndex) earcon.play();
          return nextIndex;
        });
        break;
      case 'Home':
        setFocusableIndex((prevIndex) => {
          if (prevIndex === 0) earcon.play();
          return 0;
        });
        break;
      case 'End':
        setFocusableIndex((prevIndex) => {
          if (prevIndex + 1 === numberOfTiles) earcon.play();
          return numberOfTiles - 1;
        });
        break;
      case 'PageDown':
        setFocusableIndex((prevIndex) => {
          const nextIndex = Math.min(numberOfTiles - 1, prevIndex + tilesPerPage);
          if (prevIndex === nextIndex) earcon.play();
          return nextIndex;
        });
        break;
      case 'PageUp':
        setFocusableIndex((prevIndex) => {
          const nextIndex = Math.max(0, prevIndex - tilesPerPage);
          if (prevIndex === nextIndex) earcon.play();
          return nextIndex;
        });
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  useEffect(() => {
    const focusableTile = tileContainerRefs[focusableIndex]?.firstElementChild as HTMLElement;
    // Focus on the tile if it does not already have focus and one of the tiles has the focus
    if (
      !focusableTile?.contains(document.activeElement) &&
      scrollableContentRef.current?.contains(document.activeElement)
    ) {
      focusableTile?.focus();
    }

    // FIXME: no need to update tabindex using setAttribute after props.children is removed
    // Update the tabindex so that only the currently focusable tile has tabindex of 0
    for (const [index, tileContainer] of tileContainerRefs.entries()) {
      tileContainer?.firstElementChild?.setAttribute(
        'tabindex',
        index === focusableIndex ? '0' : '-1'
      );
    }
  }, [focusableIndex, tileContainerRefs]);

  useEffect(() => {
    if (numberOfTiles > 100) {
      logger.debug('TileCarousel is limited to 100 tiles');
    }
  }, [numberOfTiles]);

  useEffect(() => {
    // When the last tile is focusable and removed, set focus on the new last tile
    setFocusableIndex((prev) => Math.min(numberOfTiles - 1, prev));
    // When the last tile is removed, scroll to the new last tile
    setScrollIndex((prev) => Math.min(numberOfTiles - 1, prev));
    // When the last tile is anchor and removed, set the new last tile as anchor
    selectionAnchorIndexRef.current = Math.min(numberOfTiles - 1, selectionAnchorIndexRef.current);
  }, [numberOfTiles]);

  const getTileContainerProps = (index: number): DOMAttributes<HTMLDivElement> => ({
    onFocus: () => {
      setFocusableIndex(index);
      setScrollIndex(index);
    },
  });

  const pageIndicator = showPageIndicator ? (
    <PageIndicator
      totalPages={numberOfPages}
      currentPageIndex={Math.floor(scrollIndex / tilesPerPage)}
      onPageTo={(pageIndex) => {
        setScrollIndex(pageIndex * tilesPerPage);
      }}
      ariaLabel={props.pageIndicatorAriaLabel}
      tooltip={props.pageIndicatorTooltip}
    />
  ) : null;

  const scrollableCurrentIndex = showPageIndicator
    ? Math.floor(scrollIndex / tilesPerPage)
    : scrollIndex;

  useEffect(() => {
    if (!showPageIndicator) return;
    const onScrollPositionChange = props.onScrollPositionChange;
    const firstTileOnPageIndex = scrollableCurrentIndex * tilesPerPage;
    const lastTileOnPageIndex = Math.min(
      numberOfTiles - 1,
      firstTileOnPageIndex + tilesPerPage - 1
    );
    onScrollPositionChange?.({
      tileIndices: [...Array(lastTileOnPageIndex - firstTileOnPageIndex + 1)].map(
        (_, i) => firstTileOnPageIndex + i
      ),
      pageIndex: scrollableCurrentIndex,
      numberOfPages,
    });
    // If the current focusable tile is not visible on the current page,
    // update the focusable tile to be the first tile on the page after scrolling
    setFocusableIndex((prevFocusableIndex) => {
      return prevFocusableIndex >= firstTileOnPageIndex && prevFocusableIndex <= lastTileOnPageIndex
        ? prevFocusableIndex
        : firstTileOnPageIndex;
    });
  }, [
    scrollableCurrentIndex,
    showPageIndicator,
    tilesPerPage,
    numberOfPages,
    numberOfTiles,
    props.onScrollPositionChange,
  ]);

  const addButtonIconType = props.addButtonProps?.iconType ?? 'add';

  return (
    <div
      style={{ ...tokens, ...props.style }}
      className={cx(cn.carouselContainer, props.className)}
      data-testid={props.testId}
    >
      {numberOfTiles > 0 ? (
        <Flow orientation="horizontal" spacing="m" className={cn.innerContainer}>
          <Flow orientation="vertical" spacing="xs" className={cn.scrollableContainer}>
            {pageIndicatorLocation === 'above' ? pageIndicator : null}
            <div onKeyDown={handleKeyDown}>
              <Scrollable
                // Because page indicator is not considered as part of Scrollable, set selectedIndex the same as currentIndex
                // so that the scrolling happens when scrollIndex is updated via page indicator
                selectedIndex={scrollableCurrentIndex}
                currentIndex={scrollableCurrentIndex}
                setCurrentIndex={(setCurrentIndex) => {
                  if (typeof setCurrentIndex === 'function') {
                    setScrollIndex((prev) =>
                      showPageIndicator
                        ? setCurrentIndex(prev) * tilesPerPage
                        : setCurrentIndex(prev)
                    );
                  } else {
                    const index = setCurrentIndex;
                    setScrollIndex(showPageIndicator ? index * tilesPerPage : index);
                  }
                }}
                contentProps={{
                  'aria-label': ariaLabel,
                  role: props.disableInteractiveMode ? 'listbox' : 'group',
                }}
                scrollWindowRef={setScrollWindowRef}
                scrollWindowClassName={cn.scrollWindow}
                contentRef={scrollableContentRef}
                variant={props.variant}
                testId={postfixTestId(props.testId, 'scrollable')}
              >
                {showPageIndicator
                  ? groupTilesByPage(tileNodes, tilesPerPage, tileWidth).map(
                      (tilePage, pageIndex) => (
                        <Flow
                          spacing="none"
                          key={pageIndex}
                          // Leave some room to prevent Scrollable from shaking when re-rendered
                          style={
                            scrollWindowWidth ? { width: `${scrollWindowWidth - 8}px` } : undefined
                          }
                          className={cn.pageContainer}
                        >
                          {tilePage.map((tile, currPageTileIndex) => {
                            const tileIndex = pageIndex * tilesPerPage + currPageTileIndex;
                            return (
                              <div
                                key={currPageTileIndex}
                                ref={createTileContainerRef(tileIndex)}
                                {...getTileContainerProps(tileIndex)}
                              >
                                {tile}
                              </div>
                            );
                          })}
                        </Flow>
                      )
                    )
                  : tileNodes.map((tile, index) => (
                      <div
                        key={index}
                        ref={createTileContainerRef(index)}
                        {...getTileContainerProps(index)}
                      >
                        {tile}
                      </div>
                    ))}
              </Scrollable>
            </div>

            {pageIndicatorLocation === 'below' ? pageIndicator : null}
          </Flow>
          {showAddButton ? (
            <Button
              tooltip={addButtonTooltip}
              ariaLabel={addButtonTooltip}
              {...props.addButtonProps}
              testId={postfixTestId(props.testId, 'addTileButton')}
              icon={{ icon: addButtonIconType === 'addToInsights' ? AddToInsights : Add }}
              variant={ButtonVariant.Icon}
              disabled={numberOfTiles >= 100}
              className={cn.addButton}
              onPress={onAddTile}
            />
          ) : null}
        </Flow>
      ) : (
        <ZeroState
          testId={postfixTestId(props.testId, 'zeroState')}
          text={
            props.zeroStateMsg ??
            (props.useAddZeroStateMsg
              ? localize('carousel.zeroState.noItemsAdded.label')
              : localize('carousel.zeroState.noItems.label'))
          }
          style={{ height: `${zeroStateHeight}px` }}
          className={cn.zeroState}
          actionButtonOneProps={
            showAddButton
              ? {
                  tooltip: addButtonTooltip,
                  ariaLabel: addButtonTooltip,
                  ...props.addButtonProps,
                  text: localize('addTile.zeroState.button.label'),
                  testId: postfixTestId(props.testId, 'zeroState-addButton'),
                  onPress: onAddTile,
                }
              : undefined
          }
        />
      )}
    </div>
  );
});

const getCarouselA11yInfo = (
  localize: Localizer,
  { selectionMode = 'multi', ...props }: TileCarouselProps
) => {
  const carouselAriaLabel = props.ariaLabel ?? localize('carousel.aria');
  const ariaLabels = [carouselAriaLabel];

  if (selectionMode === 'single') {
    ariaLabels.push(localize('selectionMode.single.aria'));
  } else if (selectionMode === 'multi' || selectionMode === 'desktop') {
    ariaLabels.push(localize('selectionMode.multi.aria'));
  }

  if (!props.disableInteractiveMode) {
    ariaLabels.push(localize('interactiveMode.hint.aria'));
  }

  // TODO: modify if all tiles are object tiles or all are presentation tiles and
  // include selection mode info.  This is not implemented yet.

  return { ariaLabel: ariaLabels.join(', ') };
};

const DEFAULT_TILE_WIDTH = 160;

const getTileWidth = (tileContainer: HTMLDivElement | null): number => {
  if (!tileContainer) return 0;

  const { marginLeft, marginRight, paddingLeft, paddingRight } = window.getComputedStyle(
    tileContainer as Element
  );
  const marginX = parseFloat(marginLeft) + parseFloat(marginRight);
  const paddingX = parseFloat(paddingLeft) + parseFloat(paddingRight);

  return tileContainer?.offsetWidth ?? DEFAULT_TILE_WIDTH + marginX + paddingX;
};

const groupTilesByPage = (
  tiles: ReadonlyArray<ReactNode>,
  tilesPerPage: number,
  tileWidth: number
): ReactNode[][] => {
  const numberOfTiles = tiles.length;
  const tilePages: ReactNode[][] = [];
  for (const [index, child] of tiles.entries()) {
    if (index >= 100) break; // TileCarousel is limited to 100 tiles
    if (index % tilesPerPage === 0) {
      // Create a new tile page
      tilePages.push([child]);
    } else {
      // Add this tile to the previous tile page
      tilePages[tilePages.length - 1].push(child);
    }

    if (index === numberOfTiles - 1) {
      const numberOfTilesInLastPage = tilePages[tilePages.length - 1].length;
      // Fill the last page with empty tile placeholder
      tilePages[tilePages.length - 1].push(
        ...new Array(tilesPerPage - numberOfTilesInLastPage).fill(
          <div style={{ width: tileWidth }} />
        )
      );
    }
  }
  return tilePages;
};

const PageIndicator = (props: PageIndicatorProps) => {
  const localize = useTextLocalizer();
  const dt = useDesignTokens();
  const isRTL = useIsRTL();
  const [focusedIndex, setFocusedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    refs: pageIndicatorDotRefs,
    createSetRef: createPageIndicatorDotRef,
  } = useRefArray<HTMLSpanElement>();

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;

    switch (event.key) {
      case 'ArrowDown': /* falls-through */
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        setFocusedIndex((prevIndex) => Math.min(props.totalPages - 1, prevIndex + 1));
        break;
      case 'ArrowUp': /* falls-through */
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        setFocusedIndex((prevIndex) => Math.max(0, prevIndex - 1));
        break;
      case 'Home':
        setFocusedIndex(0);
        break;
      case 'End':
        setFocusedIndex(props.totalPages - 1);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  useEffect(() => {
    // Focus on the dot if it does not already have focus and one of the dots has the focus
    if (
      !pageIndicatorDotRefs[focusedIndex]?.contains(document.activeElement) &&
      containerRef.current?.contains(document.activeElement)
    ) {
      pageIndicatorDotRefs[focusedIndex]?.focus();
    }
  }, [focusedIndex, pageIndicatorDotRefs]);

  return (
    <Flow
      className={cn.pageIndicator}
      aria-label={localize('pageIndicator.aria')}
      spacing="xs"
      role="radiogroup"
      onKeyDown={handleKeyDown}
      ref={containerRef}
    >
      {[...Array(props.totalPages)].map((_, index) => {
        const selected = index === props.currentPageIndex;
        const tooltip =
          props.tooltip?.(index) ??
          localize('pageIndicator.dot.aria', {
            number: (index + 1).toString(),
          });
        return (
          <div
            key={index}
            role="radio"
            tabIndex={-1}
            ref={createPageIndicatorDotRef(index)}
            className={cn.pageIndicatorDot}
            title={tooltip}
            aria-label={props.ariaLabel?.(index) ?? tooltip}
            aria-checked={selected}
            onFocus={() => {
              setFocusedIndex(index);
            }}
            onClick={() => {
              props.onPageTo(index);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                props.onPageTo(index);
              }
            }}
          >
            <Icon
              icon={selected ? PageControlDotSelected : PageControlDot}
              tokens={() => ({
                'default-fg': selected ? dt.c.colorSecondary : dt.c.colorTextDefault,
              })}
              ariaHidden
            />
          </div>
        );
      })}
    </Flow>
  );
};
